import { Award, Bell } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  fetchNotifications,
  markRead,
  type NotificationFilter,
  type NotificationView,
  shareUnread,
  shortAgo,
} from '../../lib/notifications';
import { Avatar } from '../community/Avatar';
import { GameIcon } from '../games/GameArt';
import { Alert } from '../ui';

const FILTERS: { value: NotificationFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'games', label: 'Games' },
  { value: 'communities', label: 'Communities' },
];

function Icon({ icon }: { icon: NotificationView['icon'] }) {
  if (icon.kind === 'person') return <Avatar name={icon.name} size="lg" />;
  if (icon.kind === 'achievement') {
    return (
      <span
        aria-hidden="true"
        className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand text-ink"
      >
        <Award size={22} strokeWidth={2} />
      </span>
    );
  }
  return (
    <GameIcon game={{ name: icon.name, iconUrl: null }} className="size-11 rounded-xl text-sm" />
  );
}

function Row({
  item,
  onOpen,
}: {
  item: NotificationView;
  onOpen: (item: NotificationView) => void;
}) {
  return (
    <li>
      <Link
        to={item.path}
        onClick={() => onOpen(item)}
        className={`flex gap-3 rounded-[14px] p-2.5 ${item.read ? 'hover:bg-field' : 'bg-cream hover:bg-[#ffecc4]'}`}
      >
        <Icon icon={item.icon} />
        <span className="min-w-0 flex-1 leading-[21px]">
          <b className="block font-semibold">{item.title}</b>
          <span className="block truncate text-[13px] text-muted">{item.detail}</span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1.5 text-xs text-muted">
          {shortAgo(item.createdAt)}
          {!item.read && <span className="size-2.5 rounded-full bg-ink" />}
          {!item.read && <span className="sr-only">New</span>}
        </span>
      </Link>
    </li>
  );
}

function Group({
  title,
  items,
  onOpen,
}: {
  title: string;
  items: NotificationView[];
  onOpen: (item: NotificationView) => void;
}) {
  if (!items.length) return null;
  return (
    <>
      <h3 className="mx-2.5 mt-3 mb-1.5 text-xs font-semibold tracking-[0.06em] text-muted uppercase first:mt-1">
        {title}
      </h3>
      <ul>
        {items.map((item) => (
          <Row key={item.id} item={item} onOpen={onOpen} />
        ))}
      </ul>
    </>
  );
}

type State =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; items: NotificationView[]; hasMore: boolean };

/**
 * The bell's list (M33), in the dropdown on a computer and on /notifications: All, Games or
 * Communities, new ones first, then earlier ones, from the last 30 days. Opening one marks it
 * read; `onOpen` lets the dropdown close.
 */
export function NotificationList({ onOpen }: { onOpen?: () => void }) {
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [state, setState] = useState<State>({ status: 'loading' });
  const [more, setMore] = useState(false);

  const load = useCallback((which: NotificationFilter) => {
    setState({ status: 'loading' });
    fetchNotifications(which)
      .then((page) => {
        setState({ status: 'ready', items: page.notifications, hasMore: page.hasMore });
        shareUnread(page.unread);
      })
      .catch(() => setState({ status: 'failed' }));
  }, []);
  useEffect(() => load(filter), [filter, load]);

  const items = state.status === 'ready' ? state.items : [];
  const open = (item: NotificationView) => {
    if (!item.read) {
      markRead([item.id])
        .then((result) => shareUnread(result.unread))
        .catch(() => undefined);
    }
    onOpen?.();
  };
  const readAll = () => {
    if (state.status !== 'ready') return;
    setState({ ...state, items: state.items.map((item) => ({ ...item, read: true })) });
    markRead()
      .then((result) => shareUnread(result.unread))
      .catch(() => undefined);
  };
  const loadMore = () => {
    const last = items.at(-1);
    if (state.status !== 'ready' || !last) return;
    setMore(true);
    fetchNotifications(filter, last.createdAt)
      .then((page) =>
        setState({
          status: 'ready',
          items: [...state.items, ...page.notifications],
          hasMore: page.hasMore,
        }),
      )
      .catch(() => undefined)
      .finally(() => setMore(false));
  };

  return (
    <>
      <div className="flex items-center gap-1.5 px-3.5 pb-2.5">
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
            className={`h-9 rounded-full px-3.5 text-[13px] font-semibold ${filter === value ? 'bg-ink text-card' : 'border border-line bg-card text-ink hover:border-muted'}`}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={readAll}
          disabled={!items.some((item) => !item.read)}
          className="ml-auto h-9 px-1 text-[13px] font-semibold underline decoration-brand decoration-2 underline-offset-[3px] hover:decoration-ink disabled:text-muted disabled:no-underline"
        >
          Mark all read
        </button>
      </div>
      <div className="px-2 pb-3">
        {state.status === 'loading' && (
          <p role="status" className="px-2.5 py-6 text-center text-sm text-muted">
            Loading…
          </p>
        )}
        {state.status === 'failed' && (
          <div className="px-1.5 py-2">
            <Alert>Couldn’t load your notifications. Try again in a moment.</Alert>
          </div>
        )}
        {state.status === 'ready' && items.length === 0 && (
          <div className="px-6 py-10 text-center">
            <span
              aria-hidden="true"
              className="inline-flex size-14 items-center justify-center rounded-full bg-field text-muted"
            >
              <Bell size={22} strokeWidth={1.9} />
            </span>
            <p className="mt-3 font-semibold">Nothing here yet</p>
            <p className="mt-1 text-sm leading-5 text-muted">
              {filter === 'communities'
                ? 'Replies to you and posts that mention you show here.'
                : 'Your turns, matches, replies and achievements show here.'}
            </p>
          </div>
        )}
        <Group title="New" items={items.filter((item) => !item.read)} onOpen={open} />
        <Group title="Earlier" items={items.filter((item) => item.read)} onOpen={open} />
        {state.status === 'ready' && state.hasMore && (
          <div className="pt-2 text-center">
            <button
              type="button"
              onClick={loadMore}
              disabled={more}
              className="h-10 rounded-full border border-line bg-card px-4 text-sm font-semibold hover:border-muted disabled:opacity-60"
            >
              {more ? 'Loading…' : 'Show more'}
            </button>
          </div>
        )}
        {state.status === 'ready' && items.length > 0 && !state.hasMore && (
          <p className="mx-2.5 mt-4 mb-1 text-center text-[13px] text-muted">
            That’s everything from the last 30 days.
          </p>
        )}
      </div>
    </>
  );
}
